/**
 * ComparisonRow — two-column row showing check-in vs check-out values.
 *
 * Highlights checkout value in red if it's worse (e.g. lower fuel, higher km).
 */
import { View, Text } from 'react-native'
import { FONTS, SPACING } from '@/lib/brand'
import { useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { useTranslation } from 'react-i18next'

interface ComparisonRowProps {
  label: string
  checkinValue: string | number
  checkoutValue: string | number
  unit?: string
  /** When true, the checkout value is colored red to indicate a worse state */
  isWorse?: boolean
}

export function ComparisonRow({
  label,
  checkinValue,
  checkoutValue,
  unit = '',
  isWorse = false,
}: ComparisonRowProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { t } = useTranslation()

  const formatValue = (v: string | number) => `${v}${unit ? ` ${unit}` : ''}`

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: SPACING.sm,
        gap: SPACING.sm,
      }}
    >
      {/* Label */}
      <View style={{ flex: 1 }}>
        <Text style={{ fontFamily: FONTS.body, fontSize: 14, color: colors.textSecondary }}>
          {label}
        </Text>
      </View>

      {/* Check-in value */}
      <View style={{ flex: 1, alignItems: 'center' }}>
        <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textMuted }}>
          {t('trip_checkout.checkin_value')}
        </Text>
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 16, color: colors.textPrimary }}>
          {formatValue(checkinValue)}
        </Text>
      </View>

      {/* Check-out value */}
      <View style={{ flex: 1, alignItems: 'center' }}>
        <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textMuted }}>
          {t('trip_checkout.checkout_value')}
        </Text>
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 16,
            color: isWorse ? colors.error : colors.textPrimary,
          }}
        >
          {formatValue(checkoutValue)}
        </Text>
      </View>
    </View>
  )
}
